import './styles.sass';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { pick } from 'lodash-es';

import * as QueryParams from 'components/Explorer/models/query_params';
import useQueryParams from 'hooks/useQueryParams';
import useNavigateParams from 'hooks/useNavigateParams';
import FilterSet from 'components/Explorer/models/filter_set';
import ErrorBoundary from 'components/Explorer/ErrorBoundary';

import DateSearchFields from './DateSearchFields';
import InstitutionalToggle from './InstitutionalToggle';
import InstitutionSearchFields from './InstitutionSearchFields';
import IntegrationsSearchFields from './IntegrationsSearchFields';
import OutputSearchFields from './OutputSearchFields';
import PublicationSearchFields from './PublicationSearchFields';
import SearchTerm from 'models/SearchTerm';

import { defaultSearchScope } from '../util/default_search_scope';

const DIALOG_CLOSED_EVENT = 'Advanced Search: Dialogue Closed';

const analyticsScopeName = (scope) => {
  switch (scope) {
    case 'institution':
      return 'institution';
    case 'all':
      return 'full';
    default:
      return 'undefined';
  }
};

const AdvancedSearch = ({ onClose, initialSearchTerm }) => {
  const params = useQueryParams();
  const navigateParams = useNavigateParams();

  const containerRef = useRef(null);
  const previouslyFocusedRef = useRef(null);
  // FilterSet is mutated in place by child filter components, so we keep the
  // same instance across renders and bump a counter to trigger re-renders when
  // a child mutates it.
  const [, forceUpdate] = useState(0);
  const filterSetRef = useRef(null);
  if (filterSetRef.current === null) {
    filterSetRef.current = new FilterSet(params.filters);
  }
  const filterSet = filterSetRef.current;
  // Fields keep their own state (saved summaries, drafts) bound to the
  // FilterSet they were given, so clearing every field remounts them against
  // the new one.
  const [fieldsGeneration, setFieldsGeneration] = useState(0);
  // The term Advanced Search was opened with (from a chip) prefills the first
  // set of fields only; clearing every field shouldn't bring it back.
  const fieldsSearchTerm = fieldsGeneration === 0 ? initialSearchTerm : SearchTerm.empty();

  const currentScope = filterSet.get('scope') || defaultSearchScope(window.current_user);

  // A field that saves on blur (the identifier list) can still be saving
  // when Run search is pressed, so the search waits for the save; searching
  // straight away would drop its filter. Clearing every field abandons the
  // save.
  const saveInFlightRef = useRef(null);
  const searchAwaitingSaveRef = useRef(null);
  const trackSave = (save) => {
    saveInFlightRef.current = save;
    save.finally(() => {
      if (saveInFlightRef.current === save) saveInFlightRef.current = null;
    });
  };
  // Nor can it search with a draft that can't be saved.
  const [unsavableDraft, setUnsavableDraft] = useState(false);
  const identifierDraftRef = useRef(null);

  useEffect(() => {
    previouslyFocusedRef.current = document.activeElement;

    Analytics.trackEvent('Advanced Search: Dialogue Opened', {
      scope: analyticsScopeName(currentScope)
    });

    const container = containerRef.current;
    if (container && 'showModal' in container) {
      container.showModal();
    }

    const closeOnEscape = (event) => {
      if (event.key === 'Escape') {
        hide();
        Analytics.trackEvent(DIALOG_CLOSED_EVENT, { method: 'Escape Key Press' });
      }
    };
    window.addEventListener('keydown', closeOnEscape);

    container?.querySelector('input')?.focus();

    return () => window.removeEventListener('keydown', closeOnEscape);
  }, []);

  useEffect(
    () => () => {
      searchAwaitingSaveRef.current = null;
    },
    []
  );

  const hide = () => {
    const container = containerRef.current;
    if (container && 'close' in container) container.close();
    onClose();
    previouslyFocusedRef.current?.focus();
  };

  const close = () => {
    hide();
    Analytics.trackEvent(DIALOG_CLOSED_EVENT, { method: 'Close Button' });
  };

  const cancel = () => {
    hide();
    Analytics.trackEvent(DIALOG_CLOSED_EVENT, { method: 'Cancel Button' });
  };

  const applySearch = () => {
    hide();
    Analytics.trackEvent('Advanced Search: Search Applied');

    navigateParams(QueryParams.withFilters(params, filterSet.populatedFilters));
  };

  const onSubmit = (e) => {
    e.preventDefault();
    // Run search is aria-disabled rather than disabled so it keeps focus,
    // which leaves return in any other field still able to submit.
    if (unsavableDraft) return;
    // Pressing Run search doesn't always move focus out of the identifier
    // field first, so its draft may not be saving yet.
    identifierDraftRef.current?.finish();
    const save = saveInFlightRef.current;
    if (!save) {
      applySearch();
      return;
    }
    if (searchAwaitingSaveRef.current === save) return;
    searchAwaitingSaveRef.current = save;
    save.then((saved) => {
      if (searchAwaitingSaveRef.current !== save) return;
      searchAwaitingSaveRef.current = null;
      if (saved) applySearch();
    });
  };

  const clearFields = () => {
    Analytics.trackEvent('Advanced Search: All-Fields Cleared');

    const possibleMentionFilterNames = [
      ...window.site_config.mentionFilterParams,
      ...window.site_config.mentionFilterParams.map((name) => name + '[]')
    ];
    const preservedMentionsFilters = pick(QueryParams.build(params), possibleMentionFilterNames);
    const defaultScope = { scope: defaultSearchScope(window.current_user) };
    const nextFilters = { ...preservedMentionsFilters, ...defaultScope };
    filterSetRef.current = new FilterSet(nextFilters);
    saveInFlightRef.current = null;
    searchAwaitingSaveRef.current = null;
    setFieldsGeneration((generation) => generation + 1);
  };

  const changeScope = (newScope) => {
    Analytics.trackEvent('Advanced Search: Scope Changed', {
      scope: analyticsScopeName(newScope)
    });
    filterSet.set('scope', newScope);
    forceUpdate((n) => n + 1);
  };

  const institutionContent = (
    <div className="Explorer-AdvancedSearch-fields">
      <div className="Explorer-AdvancedSearch-column">
        <InstitutionSearchFields filterSet={filterSet} initialSearchTerm={fieldsSearchTerm} />
        <PublicationSearchFields filterSet={filterSet} initialSearchTerm={fieldsSearchTerm} />
        <IntegrationsSearchFields filterSet={filterSet} />
      </div>

      <div className="Explorer-AdvancedSearch-column">
        <InstitutionalToggle
          scope={filterSet.get('scope') || defaultSearchScope(window.current_user)}
          changeScope={changeScope}
        />
        <OutputSearchFields
          filterSet={filterSet}
          initialSearchTerm={fieldsSearchTerm}
          trackSave={trackSave}
          reportUnsavableDraft={setUnsavableDraft}
          identifierDraftRef={identifierDraftRef}
        />
        <DateSearchFields filterSet={filterSet} />
      </div>
    </div>
  );

  const generalContent = (
    <div className="Explorer-AdvancedSearch-fields">
      <div className="Explorer-AdvancedSearch-column">
        <OutputSearchFields
          filterSet={filterSet}
          initialSearchTerm={fieldsSearchTerm}
          trackSave={trackSave}
          reportUnsavableDraft={setUnsavableDraft}
          identifierDraftRef={identifierDraftRef}
        />
        <IntegrationsSearchFields filterSet={filterSet} />
      </div>

      <div className="Explorer-AdvancedSearch-column">
        <PublicationSearchFields filterSet={filterSet} initialSearchTerm={fieldsSearchTerm} />
        <DateSearchFields filterSet={filterSet} />
      </div>
    </div>
  );

  // Fields write their picks straight into the filter set, and some reload
  // what they show from the URL when they re-render, which would drop picks
  // made since. Only Run search depends on save progress, so the fields
  // re-render only when they are remounted or the scope changes.
  const fieldsContent = useMemo(
    () => (window.current_user.institution_integrated ? institutionContent : generalContent),
    [fieldsGeneration, currentScope]
  );

  return (
    <dialog ref={containerRef} className="Explorer-AdvancedSearch-dialog">
      <form method="dialog" className="Explorer-Form Explorer-AdvancedSearch" onSubmit={onSubmit}>
        <ReactTooltip id="HelpLink" effect="solid" />

        <div className="Explorer-AdvancedSearch-content">
          <h2 className="Explorer-AdvancedSearch-title">{I18n.t('Explorer.AdvancedSearch.title')}</h2>

          <button type="button" className="Explorer-AdvancedSearch-close-button" onClick={close}>
            <span className="label">{I18n.t('Explorer.AdvancedSearch.close')}</span>
          </button>

          {/* Closing unmounts the dialog, so reopening it after a failure starts afresh */}
          <ErrorBoundary key={fieldsGeneration} message={I18n.t('Explorer.AdvancedSearch.fields_error')}>
            {fieldsContent}
          </ErrorBoundary>
        </div>

        <div className="Explorer-AdvancedSearch-buttons">
          <button type="button" className="cancel" onClick={cancel}>
            {I18n.t('Explorer.AdvancedSearch.cancel')}
          </button>

          <div>
            <button type="reset" className="clear" onClick={clearFields}>
              {I18n.t('Explorer.AdvancedSearch.clear')}
            </button>
            <button type="submit" className="execute" aria-disabled={unsavableDraft || undefined}>
              {I18n.t('Explorer.AdvancedSearch.search')}
            </button>
          </div>
        </div>
      </form>
    </dialog>
  );
};

AdvancedSearch.propTypes = {
  onClose: PropTypes.func.isRequired,
  initialSearchTerm: PropTypes.instanceOf(SearchTerm).isRequired
};

export default AdvancedSearch;
